import { makeAutoObservable, runInAction } from "mobx";
import { i18next } from "../i18n";
import { errorMessage } from "../lib/format";
import {
	connectDmClient,
	disconnectDmClient,
	hiveUserIdFromStream,
	listDirectConversations,
	loadDirectMessages,
	markDirectRead,
	onStreamEvent,
	openDirectChannel,
	openGroupChannel,
	sendDirectMessage,
	type DmConversation,
	type DmMessage,
} from "../lib/directMessages";
import type { FriendSummary } from "../lib/social";

function eventTime(value: unknown): string {
	if (Object.prototype.toString.call(value) === "[object Date]")
		return (value as Date).toISOString();
	if (typeof value === "string") return value;
	return new Date().toISOString();
}

function channelIdOf(event: {
	cid?: string;
	channel_id?: string;
	channel?: { id?: string; cid?: string };
}): string | null {
	const raw = event.cid ?? event.channel?.cid ?? event.channel_id ?? event.channel?.id ?? "";
	if (!raw) return null;
	return raw.includes(":") ? raw.split(":")[1] : raw;
}

export class DmStore {
	userId: string | null = null;
	streamUserId: string | null = null;
	conversations: DmConversation[] = [];
	messagesByChannelId = new Map<string, DmMessage[]>();
	activeChannelId: string | null = null;
	loadingList = false;
	loadingMessages = false;
	sending = false;
	error = "";
	private unsubscribe: (() => void) | null = null;
	private connectionStarted = false;
	private watchedFriendsKey = "";
	private friendByUserId = new Map<string, FriendSummary>();

	constructor() {
		makeAutoObservable(this, {}, { autoBind: true });
	}

	get activeMessages() {
		return this.activeChannelId ? (this.messagesByChannelId.get(this.activeChannelId) ?? []) : [];
	}

	get unreadCount() {
		return this.conversations.reduce((sum, item) => sum + item.unreadCount, 0);
	}

	isOwnMessage(userId: string) {
		return userId === this.userId || userId === this.streamUserId;
	}

	setActiveChannel(channelId: string | null) {
		this.activeChannelId = channelId;
	}

	async start(userId: string) {
		if (this.connectionStarted && this.userId === userId) return;
		if (this.connectionStarted) this.reset();
		this.userId = userId;
		this.connectionStarted = true;
		try {
			const stream = await connectDmClient();
			this.streamUserId = stream.userID ?? null;
			this.ensureSubscription();
		} catch (error) {
			runInAction(() => {
				this.error = errorMessage(error, i18next.t("dm.loadFailed"));
			});
		}
	}

	async refreshConversations(friends: FriendSummary[]) {
		this.loadingList = true;
		this.error = "";
		try {
			await this.watchFriends(friends, true);
		} finally {
			runInAction(() => {
				this.loadingList = false;
			});
		}
	}

	async openWithFriend(friend: FriendSummary): Promise<string | null> {
		this.error = "";
		try {
			const channel = await openDirectChannel(friend);
			const channelId = channel.id ?? null;
			if (!channelId) throw new Error("missing_channel_id");
			runInAction(() => {
				this.activeChannelId = channelId;
				this.upsertConversation({
					id: channelId,
					peerUserId: friend.userId,
					peerHandle: friend.handle,
					peerName: friend.displayName || friend.handle,
					peerAvatarUrl: friend.avatarUrl,
					isGroup: false,
					members: [
						{
							userId: friend.userId,
							handle: friend.handle,
							displayName: friend.displayName || friend.handle,
							avatarUrl: friend.avatarUrl,
						},
					],
					lastMessage: "",
					updatedAt: null,
					unreadCount: 0,
				});
			});
			await this.loadMessages(channelId);
			this.ensureSubscription();
			return channelId;
		} catch (error) {
			runInAction(() => {
				this.error = errorMessage(error, i18next.t("dm.openFailed"));
			});
			return null;
		}
	}

	async openGroup(friends: FriendSummary[], name?: string): Promise<string | null> {
		this.error = "";
		try {
			const channel = await openGroupChannel(friends, name);
			const channelId = channel.id ?? null;
			if (!channelId) throw new Error("missing_channel_id");
			await this.watchFriends([...this.friendByUserId.values()], true);
			await this.loadMessages(channelId);
			return channelId;
		} catch (error) {
			runInAction(() => {
				this.error = errorMessage(error, i18next.t("dm.openFailed"));
			});
			return null;
		}
	}

	async loadMessages(channelId: string) {
		this.loadingMessages = true;
		this.error = "";
		try {
			const messages = await loadDirectMessages(channelId);
			runInAction(() => {
				this.activeChannelId = channelId;
				this.messagesByChannelId.set(channelId, messages);
				this.markChannelRead(channelId);
			});
			void markDirectRead(channelId);
			this.ensureSubscription();
		} catch (error) {
			runInAction(() => {
				this.error = errorMessage(error, i18next.t("dm.messagesLoadFailed"));
			});
		} finally {
			runInAction(() => {
				this.loadingMessages = false;
			});
		}
	}

	async markAllRead() {
		const unreadChannelIds = this.conversations
			.filter((item) => item.unreadCount > 0)
			.map((item) => item.id);
		if (unreadChannelIds.length === 0) return;
		runInAction(() => {
			this.conversations = this.conversations.map((item) => ({ ...item, unreadCount: 0 }));
		});
		await Promise.all(unreadChannelIds.map((channelId) => markDirectRead(channelId)));
	}

	async send(channelId: string, text: string) {
		const trimmed = text.trim();
		if (!trimmed || this.sending) return;
		const conversation = this.conversations.find((item) => item.id === channelId);
		if (conversation && !conversation.isGroup && !this.friendByUserId.has(conversation.peerUserId)) {
			this.error = i18next.t("dm.friendRequired");
			return;
		}
		this.sending = true;
		this.error = "";
		try {
			const mentionedUserIds =
				conversation?.members
					.filter((member) =>
						new RegExp(`(^|\\s)@${escapeRegExp(member.handle)}(?=\\s|$|[.,!?])`, "i").test(trimmed),
					)
					.map((member) => member.userId) ?? [];
			const message = await sendDirectMessage(channelId, trimmed, mentionedUserIds);
			runInAction(() => this.appendMessage(channelId, message));
		} catch (error) {
			runInAction(() => {
				this.error = errorMessage(error, i18next.t("dm.sendFailed"));
			});
		} finally {
			runInAction(() => {
				this.sending = false;
			});
		}
	}

	reset() {
		this.userId = null;
		this.streamUserId = null;
		this.conversations = [];
		this.messagesByChannelId = new Map();
		this.activeChannelId = null;
		this.loadingList = false;
		this.loadingMessages = false;
		this.sending = false;
		this.error = "";
		this.unsubscribe?.();
		this.unsubscribe = null;
		this.connectionStarted = false;
		this.watchedFriendsKey = "";
		this.friendByUserId = new Map();
		void disconnectDmClient();
	}

	async watchFriends(friends: FriendSummary[], force = false) {
		this.friendByUserId = new Map(friends.map((friend) => [friend.userId, friend]));
		const key = friends
			.map((friend) => friend.userId)
			.sort()
			.join(",");
		if (!force && key === this.watchedFriendsKey) return;
		try {
			const conversations = await listDirectConversations(friends);
			runInAction(() => {
				this.conversations = conversations;
				// Only once the load lands. Recording the key up front turned a single
				// Stream timeout into a permanently empty list: the guard above then
				// short-circuits every later run for the same friend set.
				this.watchedFriendsKey = key;
			});
			this.ensureSubscription();
		} catch (error) {
			// AppLayout calls this fire-and-forget, so rejecting here would only surface
			// as an unhandled rejection. The error belongs on the store either way.
			runInAction(() => {
				this.error = errorMessage(error, i18next.t("dm.loadFailed"));
			});
		}
	}

	private ensureSubscription() {
		if (this.unsubscribe) return;
		this.unsubscribe = onStreamEvent((event) => {
			if (
				event.type !== "message.new" &&
				event.type !== "notification.message_new" &&
				event.type !== "notification.mark_read"
			) {
				return;
			}
			const channelId = channelIdOf(event);
			if (!channelId) return;
			if (event.type === "notification.mark_read") {
				runInAction(() => this.markChannelRead(channelId));
				return;
			}
			if (!event.message) {
				runInAction(() => this.bumpUnread(channelId));
				return;
			}
			if (this.isOwnMessage(event.message.user?.id ?? "")) return;
			runInAction(() => {
				this.appendMessage(channelId, {
					id: event.message!.id,
					userId: hiveUserIdFromStream(event.message!.user?.id ?? ""),
					text: event.message!.text || "",
					createdAt: eventTime(event.message!.created_at),
				});
			});
		});
	}

	private appendMessage(channelId: string, message: DmMessage) {
		this.ensureConversationForMessage(channelId, message);
		const existing = this.messagesByChannelId.get(channelId) ?? [];
		const duplicated = existing.some((item) => item.id === message.id);
		if (!duplicated) this.messagesByChannelId.set(channelId, [...existing, message]);
		const unreadIncrement =
			this.isOwnMessage(message.userId) || this.activeChannelId === channelId ? 0 : 1;
		this.conversations = this.conversations.map((item) =>
			item.id === channelId
				? {
						...item,
						lastMessage: message.text,
						updatedAt: message.createdAt,
						unreadCount: duplicated ? item.unreadCount : item.unreadCount + unreadIncrement,
					}
				: item,
		);
	}

	private ensureConversationForMessage(channelId: string, message: DmMessage) {
		if (this.conversations.some((item) => item.id === channelId)) return;
		const friend = this.friendByUserId.get(message.userId);
		if (!friend) return;
		this.upsertConversation({
			id: channelId,
			peerUserId: friend.userId,
			peerHandle: friend.handle,
			peerName: friend.displayName || friend.handle,
			peerAvatarUrl: friend.avatarUrl,
			isGroup: false,
			members: [
				{
					userId: friend.userId,
					handle: friend.handle,
					displayName: friend.displayName || friend.handle,
					avatarUrl: friend.avatarUrl,
				},
			],
			lastMessage: "",
			updatedAt: null,
			unreadCount: 0,
		});
	}

	private markChannelRead(channelId: string) {
		this.conversations = this.conversations.map((item) =>
			item.id === channelId ? { ...item, unreadCount: 0 } : item,
		);
	}

	private bumpUnread(channelId: string) {
		if (this.activeChannelId === channelId) return;
		this.conversations = this.conversations.map((item) =>
			item.id === channelId ? { ...item, unreadCount: item.unreadCount + 1 } : item,
		);
	}

	private upsertConversation(conversation: DmConversation) {
		const exists = this.conversations.some((item) => item.id === conversation.id);
		this.conversations = exists
			? this.conversations.map((item) => (item.id === conversation.id ? conversation : item))
			: [conversation, ...this.conversations];
	}
}

function escapeRegExp(value: string): string {
	return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export const dmStore = new DmStore();
